import { MantineThemeOverride } from '@mantine/core'
import { doclenzBlue, doclenzTeal, doclenzGray, DoclenzTokens } from './palette'

const T = DoclenzTokens.light

/**
 * Tema claro do Doclenz Admin.
 *
 * Mesma estrutura do `vendax-promoter-admin` — paleta central em `palette.ts`, overrides de
 * componente aqui, tokens CSS em `styles/index.css` — com a paleta trocada para azul.
 *
 * A barra lateral e os destaques do `ArchbaseAdminMainLayout` derivam da cor primária do tema,
 * então trocar a paleta aqui reveste o layout inteiro.
 */
export const AppThemeLight: MantineThemeOverride = {
  primaryColor: 'appPrimary',
  primaryShade: 6,
  colors: {
    appPrimary: doclenzBlue,
    appAccent: doclenzTeal,
    appGray: doclenzGray,
    doclenzBlue,
    doclenzTeal,
  },
  // `lg` = 16px é o raio padrão do produto.
  defaultRadius: 'lg',
  radius: {
    xs: '6px',
    sm: '9px',
    md: '11px',
    lg: '16px',
    xl: '22px',
  },
  fontFamily: 'Inter, system-ui, sans-serif',
  headings: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: '700',
  },
  shadows: {
    xs: '0 4px 14px rgba(13,32,73,.04)',
    sm: '0 6px 20px rgba(13,32,73,.06)',
    md: T.shadow,
    lg: '0 14px 40px rgba(13,32,73,.10)',
    xl: '0 20px 56px rgba(13,32,73,.12)',
  },
  components: {
    AppShell: {
      styles: {
        navbar: {
          border: 'none',
          backgroundImage: DoclenzTokens.sidebarGradient,
          backgroundColor: DoclenzTokens.navy950,
        },
        header: {
          borderBottom: `1px solid ${T.border}`,
          backgroundColor: T.surface,
        },
        // O AppShell pinta a área de conteúdo com um cinza próprio, que cobriria o fundo
        // definido no body. Os halos precisam vir daqui; `fixed` impede que deslizem no scroll.
        main: {
          backgroundColor: T.bg,
          backgroundImage:
            'radial-gradient(circle at 85% 8%, rgba(30,196,212,.10), transparent 26%),' +
            'radial-gradient(circle at 15% 88%, rgba(42,99,196,.07), transparent 28%)',
          backgroundAttachment: 'fixed',
        },
      },
    },
    Card: {
      defaultProps: {
        shadow: 'md',
        radius: 'lg',
        withBorder: true,
      },
      styles: {
        root: {
          backgroundColor: T.surface,
          borderColor: T.border,
          color: T.text,
        },
      },
    },
    Paper: {
      styles: {
        root: {
          backgroundColor: T.surface,
          borderColor: T.border,
          color: T.text,
        },
      },
    },
    Button: {
      defaultProps: {
        radius: 'sm',
      },
    },
    Badge: {
      styles: {
        root: {
          textTransform: 'none',
        },
      },
    },
    Table: {
      styles: {
        th: {
          color: T.muted,
          fontWeight: 700,
          backgroundColor: T.surface2,
        },
      },
    },
    Code: {
      styles: {
        root: {
          backgroundColor: T.surface3,
          color: T.text,
        },
      },
    },
    Tooltip: {
      styles: {
        tooltip: {
          fontWeight: '500',
        },
      },
    },
  },
}

/** Cores do aplicativo, para referência rápida em componentes. */
export const AppColors = {
  primary: DoclenzTokens.blue600,
  accent: DoclenzTokens.teal,
  backgroundDark: DoclenzTokens.dark.bg,
  surface: DoclenzTokens.dark.surface,
  surfaceDark: DoclenzTokens.dark.surface2,
  backgroundLight: T.surface,
  success: '#12915a',
  error: '#ef5b4c',
  warning: '#f2c94c',
  info: DoclenzTokens.blue500,
  textPrimary: T.text,
  textSecondary: T.muted,
  gradient: DoclenzTokens.primaryGradient,
} as const

/** Cores por situação — de job e de chave de API. */
export const StatusColors = {
  ativo: AppColors.success,
  inativo: AppColors.textSecondary,
  processando: DoclenzTokens.blue500,
  concluido: AppColors.success,
  falhou: AppColors.error,
  revogado: AppColors.textSecondary,
} as const

/** Níveis de consumo de cota — o que a barra da tela de consumo usa. */
export const QuotaColors = {
  ok: DoclenzTokens.blue500,
  atencao: AppColors.warning,
  critico: AppColors.error,
} as const
